// Maps a failed POST /orders/{orderId}/returned-payment-fee (#298) into the
// charge dialog's INLINE failure copy. Only the contracted failures get the
// inline treatment — the exact set useOrderApi's suppressInlineFeeFailure hides
// from useApi's global toast: the four 402 payment codes, any 409 (each
// refusal is coded since PR 358), and the 500 fee_save_failed. Everything else
// returns undefined: those failures already surfaced through useApi's
// full-fidelity handlers, so the dialog renders nothing for them (and stays open).
//
// Modeled on Checkout/_helpers/checkoutOrderError.ts: duck-typed ApiFailure,
// { error, message } body parsed from ApiError.detail.

import { FEE_SAVE_FAILED } from "@models/order";
import {
    PAYMENT_DECLINED,
    PAYMENT_HELD,
    PAYMENT_UNAVAILABLE,
    PAYMENT_UNCONFIRMED,
} from "@models/paymentMethod";
import {
    asApiFailure,
    parseFailureBody,
    parseJsonObject,
} from "@utils/apiFailure";

// declined and unavailable are the failures that may invite a retry — no money
// moved, and the server releases the charge's claim in the same request, so
// the order is as it was (no reload). Every copy that tells the admin not to
// try again (unconfirmed, held, charged-but-not-saved, charge_not_recorded)
// also BLOCKS Charge Fee for the order in the session, at once. #469: the
// first three also leave the claim's marker on the order, which disables
// Charge Fee too, but the page sees it only once its reload lands; the block
// covers that gap, and an order read without the marker. For unconfirmed and
// held the server message does not REPLACE our do-not-retry line — it is
// added after it (round 1, finding 5). For declined and 409 the server
// message wins outright (checkout convention), with our copy as the floor.
const DECLINED_FALLBACK =
    "The charge was declined by the payment provider. No fee was collected — you can verify the saved payment method and try again.";

const UNCONFIRMED_COPY =
    "We couldn't confirm whether the fee was charged. Do not try again — verify the transaction with the payment provider first.";

const HELD_COPY =
    "The payment provider is holding this transaction for review. Do not try again — a second attempt would charge the fee twice.";

// MB-05. Our own copy: the server's names the gateway and merchant account,
// which the admin can do nothing about.
const UNAVAILABLE_COPY =
    "The payment service isn't available right now. Nothing was charged. Try again in a few minutes.";

const CONFLICT_FALLBACK =
    "This fee can't be charged — it may already have been charged, or the organization has no saved payment method.";

// The one 409 that moved money (API PR 370, #469): THIS charge went through,
// but while it was at the vendor another request resolved the order's
// unconfirmed fee (cleared it as voided, recorded another transaction, or a
// second charge claimed the order), so it is not recorded. Its own floor,
// because CONFLICT_FALLBACK ("can't be charged") says the opposite of what
// happened. It blocks Charge Fee in the session like the other do-not-retry
// answers, and here the block may be all there is: when the fee was cleared
// as voided meanwhile, the order carries neither a fee nor a marker, so
// nothing on the server stops a second charge. So its block, alone, outlives
// a clear on Unconfirmed Charges (round 1b): that clear resolves the order's
// marker, never this charge.
const CHARGE_NOT_RECORDED = "charge_not_recorded";

const CHARGE_NOT_RECORDED_FALLBACK =
    "This fee charge went through, but it isn't recorded on the order: another request resolved the order's fee " +
    "while it was being charged. Do not charge it again.";

// #469: an earlier fee charge on this order is unconfirmed (another charge's
// claim), so nothing was charged. The order reloads to show the marker, and
// this message stays beside the marker's notice so the admin knows no money
// moved (round 1b). Its own floor, because CONFLICT_FALLBACK does not say so.
const FEE_UNCONFIRMED = "fee_unconfirmed";

const FEE_UNCONFIRMED_FALLBACK =
    "Nothing was charged: an earlier fee charge on this order is still unconfirmed.";

// The 409s that prove nothing was recorded on the order. Any other 409 —
// fee_already_charged, or a code this UI does not know — may mean the order
// now carries its fee or an unconfirmed charge, so the page reloads to show
// it. charge_not_recorded and fee_unconfirmed have their own branches and
// reload too.
const NOTHING_RECORDED_CONFLICTS = [
    "payment_not_returned",
    "no_vendor_profile",
    "no_active_card",
];

// C-09. Our own copy, not the server's, so it reads like the rest of this
// dialog. #469: the charge's claim stays on the order, so the admin records it
// as settled on the Unconfirmed Charges page. It blocks Charge Fee in the
// session (see the top of this file).
const FEE_SAVE_FAILED_COPY =
    "The card was charged, but the fee wasn't saved on this order. Do not charge it again. " +
    "Record it as settled in Billing › Unconfirmed Charges";

export interface ReturnedPaymentFeeError {
    message: string;
    // The copy says not to try again: Charge Fee must stay off for this order
    // in the session, or the admin may collect the fee twice.
    blocksRecharge: boolean;
    // That block stays even when the order's unconfirmed fee is cleared on
    // Unconfirmed Charges (charge_not_recorded only). Every other block waits
    // on the charge such a clear resolves, so the clear lifts it.
    blockOutlivesClear: boolean;
    // The order may now carry its fee or an unconfirmed-charge marker on the
    // server: reload it, so the server's state hides or disables Charge Fee.
    reloadsOrder: boolean;
    // Once the marker disables Charge Fee, the dialog shows its notice in
    // place of this message, except when this is set: then the notice and
    // this message (fee_unconfirmed: nothing was charged).
    showsWithUnconfirmedNotice: boolean;
    // The vendor transaction the failure names, when the body carries one.
    transactionId?: string;
}

export const returnedPaymentFeeError = (
    error: unknown,
): ReturnedPaymentFeeError | undefined => {
    const failure = asApiFailure(error);

    if (!failure) {
        return undefined;
    }

    const body = parseFailureBody(failure.detail);
    const serverMessage = body?.message.trim() ? body.message : undefined;
    const fieldTransactionId = transactionIdField(failure.detail);

    if (failure.status === 500 && body?.error === FEE_SAVE_FAILED) {
        return {
            message: fieldTransactionId
                ? `${FEE_SAVE_FAILED_COPY}, with transaction ID ${fieldTransactionId}.`
                : `${FEE_SAVE_FAILED_COPY}, with its transaction ID from Authorize.Net.`,
            blocksRecharge: true,
            blockOutlivesClear: false,
            reloadsOrder: true,
            showsWithUnconfirmedNotice: false,
            ...(fieldTransactionId ? { transactionId: fieldTransactionId } : {}),
        };
    }

    if (failure.status === 409) {
        const code = body?.error ?? "";

        if (code === CHARGE_NOT_RECORDED) {
            return {
                message: chargeNotRecordedMessage(
                    serverMessage,
                    fieldTransactionId,
                ),
                blocksRecharge: true,
                blockOutlivesClear: true,
                reloadsOrder: true,
                showsWithUnconfirmedNotice: false,
                ...(fieldTransactionId ? { transactionId: fieldTransactionId } : {}),
            };
        }

        if (code === FEE_UNCONFIRMED) {
            return {
                message: serverMessage ?? FEE_UNCONFIRMED_FALLBACK,
                blocksRecharge: false,
                blockOutlivesClear: false,
                reloadsOrder: true,
                showsWithUnconfirmedNotice: true,
            };
        }

        return {
            message: serverMessage ?? CONFLICT_FALLBACK,
            blocksRecharge: false,
            blockOutlivesClear: false,
            reloadsOrder: !NOTHING_RECORDED_CONFLICTS.includes(code),
            showsWithUnconfirmedNotice: false,
        };
    }

    if (failure.status !== 402) {
        return undefined;
    }

    // #469 (API PR 370): unconfirmed and held leave the claim's marker, so
    // the order reloads to show it. Declined and unavailable release the claim
    // in the same request, so a reload would show nothing new.
    switch (body?.error) {
        case PAYMENT_DECLINED:
            return {
                message: serverMessage ?? DECLINED_FALLBACK,
                blocksRecharge: false,
                blockOutlivesClear: false,
                reloadsOrder: false,
                showsWithUnconfirmedNotice: false,
            };
        case PAYMENT_UNAVAILABLE:
            return {
                message: UNAVAILABLE_COPY,
                blocksRecharge: false,
                blockOutlivesClear: false,
                reloadsOrder: false,
                showsWithUnconfirmedNotice: false,
            };
        case PAYMENT_UNCONFIRMED:
            return {
                message: withServerDetail(UNCONFIRMED_COPY, serverMessage),
                blocksRecharge: true,
                blockOutlivesClear: false,
                reloadsOrder: true,
                showsWithUnconfirmedNotice: false,
            };
        case PAYMENT_HELD:
            return {
                message: withServerDetail(
                    HELD_COPY,
                    serverMessage ??
                        (fieldTransactionId
                            ? `Transaction ID ${fieldTransactionId}.`
                            : undefined),
                ),
                blocksRecharge: true,
                blockOutlivesClear: false,
                reloadsOrder: true,
                showsWithUnconfirmedNotice: false,
                ...(fieldTransactionId ? { transactionId: fieldTransactionId } : {}),
            };
        default:
            return undefined;
    }
};

// Our do-not-retry line first, then the server's message, which may name the
// transaction and where to resolve it.
const withServerDetail = (copy: string, serverMessage?: string): string =>
    serverMessage ? `${copy} ${serverMessage}` : copy;

// The server's message names the transaction to refund; the id is added when
// it does not, so the admin always has it.
const chargeNotRecordedMessage = (
    serverMessage: string | undefined,
    transactionId: string | undefined,
): string => {
    if (serverMessage) {
        return transactionId && !serverMessage.includes(transactionId)
            ? `${serverMessage} Transaction ID ${transactionId}.`
            : serverMessage;
    }

    return transactionId
        ? `${CHARGE_NOT_RECORDED_FALLBACK} Refund or void transaction ${transactionId} in Authorize.Net.`
        : `${CHARGE_NOT_RECORDED_FALLBACK} Refund or void it in Authorize.Net.`;
};

// A non-blank string, or a finite number (finding 7), as the id's text.
const transactionIdField = (detail?: string): string | undefined => {
    const transactionId = parseJsonObject(detail)?.transactionId;

    if (typeof transactionId === "number") {
        return Number.isFinite(transactionId) ? String(transactionId) : undefined;
    }

    return typeof transactionId === "string" && transactionId.trim()
        ? transactionId.trim()
        : undefined;
};
